import React, { useState, useEffect } from 'react';
import { 
    Box, Typography, TextField, Paper, Avatar, Chip, 
    Button, Grid, Table, TableBody, TableCell, 
    TableHead, TableRow 
} from '@mui/material';
import { 
    Search as SearchIcon, 
    Email as EmailIcon,
    Phone as PhoneIcon,
    LinkedIn as LinkedInIcon,
    Twitter as TwitterIcon,
    GitHub as GitHubIcon,
} from '@mui/icons-material';
import { keyframes, styled } from '@mui/system';

const bounce = keyframes`
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-5px); }
`;

const pulse = keyframes`
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
`;

const SlideContainer = styled(Box)({
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#fff',
    overflowY: 'auto',
    color: '#111827', 
    '&::-webkit-scrollbar': { width: '4px' },
    '&::-webkit-scrollbar-thumb': { backgroundColor: '#e5e7eb', borderRadius: '4px' }
});

const HeroSlider = () => {
    const [activeSlide, setActiveSlide] = useState(0);

    const slides = [
        { id: 0, label: 'AI Persona Generator' },
        { id: 1, label: 'Prospects Discovery' },
        { id: 2, label: 'Assessment' },
        { id: 3, label: 'Candidate Profile' },
        { id: 4, label: 'Ready to Connect' },
    ];

    useEffect(() => {
        const interval = setInterval(() => {
            setActiveSlide((prev) => (prev + 1) % 5);
        }, 5000);
        return () => clearInterval(interval);
    }, []);

    const renderSlideContent = () => {
        switch (activeSlide) {
            case 0: // AI Persona Generator
                return (
                    <SlideContainer sx={{ p: 2.5, pb: 12 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 2, color: '#111827', lineHeight: 1.2 }}>
                            Enter your intent to generate an ideal persona
                        </Typography>
                        <Box sx={{ position: 'relative', mb: 1 }}>
                            <TextField
                                fullWidth
                                placeholder="Describe the role, industry, and experience..."
                                variant="outlined"
                                size="small"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        bgcolor: '#f0fdf4',
                                        fontSize: '0.75rem',
                                        borderRadius: '8px',
                                        color: '#111827',
                                        '& fieldset': { borderColor: '#bbf7d0', borderWidth: 2 },
                                    },
                                    '& .MuiInputBase-input::placeholder': {
                                        color: '#6b7280',
                                        opacity: 1,
                                    }
                                }}
                            />
                            <Box sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', display: 'flex', gap: 0.5 }}>
                                {[0, 0.2, 0.4].map((d, i) => (
                                    <Box key={i} sx={{ color: '#4ade80', animation: `${bounce} 1s infinite ${d}s`, fontSize: 14 }}>★</Box>
                                ))}
                            </Box>
                        </Box>
                        <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 2 }}>✨ Powered by AI</Typography>
                        
                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#4b5563', mb: 1, display: 'block' }}>Examples:</Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {[
                                { t: 'Engineering managers from fintech...', bg: 'linear-gradient(to right, #eff6ff, #dbeafe)', b: '#bfdbfe' },
                                { t: 'Find VP sales for a sports shoe company', bg: 'linear-gradient(to right, #f5f3ff, #ede9fe)', b: '#ddd6fe' },
                                { t: 'Director of Finance salary in Atlanta', bg: 'linear-gradient(to right, #fdf2f8, #fce7f3)', b: '#fbcfe8' },
                                { t: 'How to assess a Performance Marketing Head', bg: 'linear-gradient(to right, #fff7ed, #ffedd5)', b: '#fed7aa' },
                            ].map((ex, i) => (
                                <Box key={i} sx={{ p: 1.2, background: ex.bg, border: `1px solid ${ex.b}`, borderRadius: '6px', fontSize: '0.7rem', color: '#1f2937' }}>
                                    {ex.t}
                                </Box>
                            ))}
                        </Box>
                        <Box sx={{ borderTop: '1px solid #e5e7eb', mt: 2.5, pt: 1, textAlign: 'center' }}>
                            <Typography variant="caption" sx={{ color: '#9ca3af' }}>💡 Be specific for better results</Typography>
                        </Box>
                    </SlideContainer>
                );

            case 1: // Prospects Discovery
                return (
                    <SlideContainer>
                        <Box sx={{ p: 2, bgcolor: '#f9fafb', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ bgcolor: '#10b981', p: 0.5, borderRadius: 1, display: 'flex' }}><SearchIcon sx={{ color: 'white', fontSize: 14 }} /></Box>
                            <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#111827' }}>Prospects List</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: '#fee2e2', px: 1, py: 0.2, borderRadius: 10, animation: `${pulse} 2s infinite` }}>
                                <Box sx={{ width: 6, height: 6, bgcolor: '#ef4444', borderRadius: '50%' }} />
                                <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#991b1b' }}>Live</Typography>
                            </Box>
                        </Box>
                        <Box sx={{ p: 1.5 }}>
                            <TextField 
                                fullWidth 
                                size="small" 
                                value="CTOs at fintech startups in London..." 
                                InputProps={{ 
                                    readOnly: true, 
                                    sx: { 
                                        fontSize: '0.7rem', 
                                        bgcolor: '#f9fafb',
                                        color: '#111827'
                                    } 
                                }} 
                            />
                        </Box>
                        <Box sx={{ px: 1.5, flex: 1 }}>
                            {[
                                { name: 'Alex Kumar', role: 'CTO', company: 'BlockFinance', initials: 'AK', color: '#3b82f6' },
                                { name: 'Sarah Mitchell', role: 'Former CTO', company: 'CryptoBank', initials: 'SM', color: '#8b5cf6' },
                                { name: 'Robert Johnson', role: 'Head of Eng', company: 'NeoFinance', initials: 'RJ', color: '#10b981' },
                            ].map((person, i) => (
                                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.2, mb: 1, bgcolor: '#f9fafb', borderRadius: '8px', border: '1px solid #f3f4f6' }}>
                                    <Avatar sx={{ width: 32, height: 32, fontSize: '0.75rem', bgcolor: person.color }}>{person.initials}</Avatar>
                                    <Box>
                                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#111827' }}>{person.name}</Typography>
                                        <Typography sx={{ fontSize: '0.65rem', color: '#4b5563' }}>{person.role} • {person.company}</Typography>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                        <Box sx={{ p: 1.5, borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', bgcolor: '#f9fafb' }}>
                            <Typography variant="caption" sx={{ color: '#6b7280' }}>3 of 47 results</Typography>
                            <Typography variant="caption" sx={{ color: '#10b981', fontWeight: 700 }}>View all →</Typography>
                        </Box>
                    </SlideContainer>
                );

            case 2: // Assessment
                return (
                    <SlideContainer sx={{ p: 2 }}>
                        <Typography sx={{ fontSize: '1rem', fontWeight: 800, mb: 2, color: '#111827' }}>Assessment - Alex Kumar</Typography>
                        <Table size="small" sx={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden', mb: 2 }}>
                            <TableHead sx={{ bgcolor: '#f9fafb' }}>
                                <TableRow>
                                    <TableCell sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#111827' }}>Competencies</TableCell>
                                    <TableCell align="right" sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#111827' }}>Fitment</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {[
                                    { n: 'Blockchain Architecture', s: 92 },
                                    { n: 'Team Leadership', s: 88 },
                                    { n: 'Fintech Knowledge', s: 95 },
                                    { n: 'Strategic Planning', s: 85 }
                                ].map((row, i) => (
                                    <TableRow key={i}>
                                        <TableCell sx={{ fontSize: '0.7rem', color: '#111827' }}>{row.n}</TableCell>
                                        <TableCell align="right">
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                                                <Box sx={{ width: 50, height: 6, bgcolor: '#e5e7eb', borderRadius: 4 }}>
                                                    <Box sx={{ width: `${row.s}%`, height: '100%', bgcolor: '#10b981', borderRadius: 4 }} />
                                                </Box>
                                                <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, color: '#059669' }}>{row.s}%</Typography>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        <Box sx={{ bgcolor: '#f0fdf4', p: 1.5, borderRadius: 2, border: '1px solid #bbf7d0' }}>
                            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#065f46', mb: 1 }}>Key Strengths</Typography>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                {['Leadership', 'Strategy', 'Growth'].map(s => (
                                    <Chip key={s} label={s} size="small" sx={{ height: 20, fontSize: '0.6rem', bgcolor: '#bbf7d0', color: '#065f46' }} />
                                ))}
                            </Box>
                        </Box>
                    </SlideContainer>
                );

            case 3: // Candidate Profile
                return (
                    <SlideContainer sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                            <Avatar sx={{ width: 50, height: 50, bgcolor: '#3b82f6', fontWeight: 800 }}>AK</Avatar>
                            <Box>
                                <Typography sx={{ fontSize: '1.1rem', fontWeight: 800, color: '#111827' }}>Alex Kumar</Typography>
                                <Typography sx={{ fontSize: '0.75rem', color: '#6b7280' }}>CTO • BlockFinance</Typography>
                            </Box>
                        </Box>
                        <Grid container spacing={1} sx={{ mb: 2 }}>
                            <Grid item xs={6}>
                                <Paper variant="outlined" sx={{ p: 1, textAlign: 'center' }}>
                                    <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#6b7280' }}>EXPERIENCE</Typography>
                                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 800, color: '#111827' }}>12 years</Typography>
                                </Paper>
                            </Grid>
                            <Grid item xs={6}>
                                <Paper variant="outlined" sx={{ p: 1, textAlign: 'center' }}>
                                    <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#6b7280' }}>LOCATION</Typography>
                                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 800, color: '#111827' }}>London, UK</Typography>
                                </Paper>
                            </Grid>
                        </Grid>
                        <Box sx={{ p: 1.5, border: '1px solid #e5e7eb', borderRadius: 2, mb: 1.5 }}>
                            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, mb: 1, color: '#111827' }}>CONTACT</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <EmailIcon sx={{ fontSize: 14, color: '#3b82f6' }} />
                                <Typography sx={{ fontSize: '0.7rem', color: '#111827' }}>alex.kumar@blockfinance.io</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <PhoneIcon sx={{ fontSize: 14, color: '#3b82f6' }} />
                                <Typography sx={{ fontSize: '0.7rem', color: '#111827' }}>+44 20 7946 0958</Typography>
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            {[LinkedInIcon, TwitterIcon, GitHubIcon].map((Icon, i) => (
                                <Box key={i} sx={{ bgcolor: '#eff6ff', p: 1, borderRadius: 1.5, display: 'flex' }}>
                                    <Icon sx={{ fontSize: 16, color: '#2563eb' }} />
                                </Box>
                            ))}
                        </Box>
                    </SlideContainer>
                );

            case 4: // Ready to Connect
                return (
                    <SlideContainer sx={{ p: 2, pb: 14 }}>
                        <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: '#111827' }}>Ready to Connect?</Typography>
                        <Typography sx={{ fontSize: '0.7rem', color: '#6b7280', mb: 2 }}>Take action on Alex Kumar's profile</Typography>
                        
                        <Paper sx={{ p: 1.5, border: '1px solid #e5e7eb', mb: 2 }}>
                            <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5, alignItems: 'center' }}>
                                <Avatar sx={{ width: 40, height: 40, bgcolor: '#3b82f6', fontWeight: 700, fontSize: '0.9rem' }}>AK</Avatar>
                                <Box>
                                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 800, color: '#111827' }}>Alex Kumar</Typography>
                                    <Typography sx={{ fontSize: '0.7rem', color: '#6b7280' }}>CTO • BlockFinance</Typography>
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                {['Tech Leadership', '12 Years Exp', 'London'].map(tag => (
                                    <Chip key={tag} label={tag} size="small" sx={{ fontSize: '0.6rem', height: 20, bgcolor: '#f3f4f6', color: '#111827' }} />
                                ))}
                            </Box>
                        </Paper>

                        <Grid container spacing={1} sx={{ mb: 2 }}>
                            {[
                                { l: 'Progression', v: '92%', s: 'Excellent', c: '#059669', bg: '#f0fdf4' },
                                { l: 'Stability', v: '78%', s: 'High', c: '#2563eb', bg: '#eff6ff' },
                                { l: 'Mobility', v: '68%', s: 'Open', c: '#d97706', bg: '#fffbeb' },
                            ].map((m, i) => (
                                <Grid item xs={4} key={i}>
                                    <Box sx={{ p: 1, bgcolor: m.bg, borderRadius: 1, textAlign: 'center', border: `1px solid ${m.c}30` }}>
                                        <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: m.c }}>{m.l}</Typography>
                                        <Typography sx={{ fontSize: '1.1rem', fontWeight: 800, color: m.c }}>{m.v}</Typography>
                                        <Typography sx={{ fontSize: '0.6rem', color: m.c }}>{m.s}</Typography>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Button variant="contained" fullWidth sx={{ bgcolor: '#10b981', fontSize: '0.75rem', fontWeight: 700, textTransform: 'none', py: 1.2, '&:hover': { bgcolor: '#059669' } }}>
                                Send Opportunity
                            </Button>
                            <Button variant="outlined" fullWidth sx={{ color: '#10b981', borderColor: '#10b981', fontSize: '0.75rem', fontWeight: 700, textTransform: 'none', py: 1.2, borderWidth: 2, '&:hover': { borderWidth: 2, bgcolor: '#f0fdf4' } }}>
                                Save Profile
                            </Button>
                            <Button fullWidth sx={{ bgcolor: '#f3f4f6', color: '#374151', fontSize: '0.75rem', fontWeight: 700, textTransform: 'none', py: 1.2, '&:hover': { bgcolor: '#e5e7eb' } }}>
                                View More
                            </Button>
                        </Box>
                        <Box sx={{ bgcolor: '#f9fafb', p: 1.5, borderRadius: 1, border: '1px solid #e5e7eb', mt: 1 }}>
                            <Typography sx={{ fontSize: '0.65rem', color: '#374151' }}><strong>💡 Pro Tip:</strong> Reach out within 48 hours to maximize response rate</Typography>
                        </Box>
                    </SlideContainer>
                );

            default: return null;
        }
    };

    return (
        <Box sx={{ 
            position: 'relative', 
            height: 384,
            width: '100%',
            maxWidth: 450,
            mx: 'auto',
            bgcolor: 'white', 
            borderRadius: 4, 
            overflow: 'hidden', 
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', 
            border: '1px solid #e5e7eb',
            zIndex: 1,
            color: '#111827', 
        }}>
            {renderSlideContent()}

            <Box sx={{ 
                position: 'absolute', 
                bottom: 0, 
                left: 0, 
                right: 0, 
                pb: 2, 
                pt: 4,
                textAlign: 'center',
                background: 'linear-gradient(transparent, white 40%)',
                pointerEvents: 'none'
            }}>
                <Box sx={{ mb: 1.5 }}>
                    <Typography 
                        sx={{ 
                            display: 'inline-block', 
                            bgcolor: 'white', 
                            px: 2, py: 0.5, 
                            borderRadius: 10, 
                            border: '1px solid #10b981',
                            color: '#10b981', 
                            fontSize: '0.7rem', 
                            fontWeight: 700,
                            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)'
                        }}
                    >
                        {slides[activeSlide].label}
                    </Typography>
                </Box>
                <Box sx={{ display: 'none' }}>
    {slides.map((slide, i) => (
        <Box
            key={slide.id}
            onClick={() => setActiveSlide(i)}
            sx={{
                width: activeSlide === i ? 24 : 8,
                height: 8,
                borderRadius: 4,
                bgcolor: activeSlide === i ? '#10b981' : '#e5e7eb',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
            }}
        />
    ))}
</Box>
            </Box>
        </Box>
    );
};

export default HeroSlider;